import { ReactElement } from 'react';
import {
    Dialog,
    DialogContent,
    DialogActions,
    Button,
    Typography,
    Box,
    Grid,
} from '@mui/material';
import PeopleIcon from '@mui/icons-material/People';
import EventAvailableIcon from '@mui/icons-material/EventAvailable';
import AssessmentIcon from '@mui/icons-material/Assessment';
import CardGiftcardIcon from '@mui/icons-material/CardGiftcard';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import { useTranslation } from 'react-i18next';

type WelcomeDialogProps = {
    open: boolean;
    onClose: () => void;
    onStartTour?: () => void;
};

type Feature = {
    icon: ReactElement;
    text: string;
};

export default function WelcomeDialog({
    open,
    onClose,
    onStartTour,
}: WelcomeDialogProps): React.ReactElement {
    const { t } = useTranslation();

    const handleGetStarted = (): void => {
        onClose();
        if (onStartTour) {
            setTimeout(() => {
                onStartTour();
            }, 300);
        }
    };

    const features: Feature[] = [
        {
            icon: <PeopleIcon sx={{ fontSize: 28 }} />,
            text: t('welcome.manageUsers'),
        },
        {
            icon: <EventAvailableIcon sx={{ fontSize: 28 }} />,
            text: t('welcome.onlineBooking'),
        },
        {
            icon: <AssessmentIcon sx={{ fontSize: 28 }} />,
            text: t('welcome.accountingReports'),
        },
        {
            icon: <CardGiftcardIcon sx={{ fontSize: 28 }} />,
            text: t('welcome.loyaltyBonds'),
        },
    ];

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="sm"
            fullWidth
            PaperProps={{
                sx: {
                    borderRadius: 3,
                    overflow: 'hidden',
                    maxHeight: '90vh',
                },
            }}
        >
            {/* Header */}
            <Box
                sx={{
                    textAlign: 'center',
                    background: (theme) =>
                        `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
                    // Tinta sobre lima: el blanco no se lee sobre el degradado.
                    color: 'primary.contrastText',
                    py: 4,
                    px: 3,
                }}
            >
                <Typography
                    variant="h4"
                    sx={{
                        fontWeight: 700,
                        mb: 1,
                    }}
                >
                    {t('welcome.title')}
                </Typography>
                <Typography
                    variant="body1"
                    sx={{
                        opacity: 0.95,
                        maxWidth: 400,
                        mx: 'auto',
                    }}
                >
                    {t('welcome.subtitle')}
                </Typography>
            </Box>

            {/* Content */}
            <DialogContent sx={{ p: 0 }}>
                <Box sx={{ p: 3 }}>
                    <Grid container spacing={2}>
                        {features.map((feature, index) => (
                            <Grid size={{ xs: 6 }} key={index}>
                                <Box
                                    sx={{
                                        display: 'flex',
                                        flexDirection: 'column',
                                        alignItems: 'center',
                                        textAlign: 'center',
                                        p: 2,
                                        borderRadius: 2,
                                        backgroundColor:
                                            'rgba(186, 242, 0, 0.08)',
                                        height: '100%',
                                        transition: 'all 0.2s ease',
                                        '&:hover': {
                                            backgroundColor:
                                                'rgba(186, 242, 0, 0.15)',
                                            transform: 'translateY(-2px)',
                                        },
                                    }}
                                >
                                    <Box
                                        sx={{
                                            color: 'var(--brand-text)',
                                            mb: 1,
                                        }}
                                    >
                                        {feature.icon}
                                    </Box>
                                    <Typography
                                        variant="body2"
                                        sx={{
                                            fontWeight: 500,
                                            color: 'text.primary',
                                            lineHeight: 1.3,
                                        }}
                                    >
                                        {feature.text}
                                    </Typography>
                                </Box>
                            </Grid>
                        ))}
                    </Grid>
                </Box>
            </DialogContent>

            {/* Actions */}
            <DialogActions
                sx={{
                    px: 3,
                    py: 2,
                    justifyContent: 'center',
                }}
            >
                <Button
                    onClick={handleGetStarted}
                    variant="contained"
                    color="primary"
                    size="large"
                    startIcon={<PlayArrowIcon />}
                    sx={{
                        px: 4,
                        py: 1.5,
                        borderRadius: 2,
                        fontWeight: 600,
                        textTransform: 'none',
                        fontSize: '1rem',
                    }}
                >
                    {t('welcome.closeButton')}
                </Button>
            </DialogActions>
        </Dialog>
    );
}
